// src/components/author/ManuscriptActions.jsx
import React from 'react';
import { PenTool, RotateCcw, Edit } from 'lucide-react';
import { handleStartCorrection, handleStartRevision } from '../../utils/submissionUtility';

/**
 * Determine which action(s) are available for a given submission status.
 */
export const getAvailableActions = (status) => ({
  continue: ['draft', 'saved', 'drafted', 'revision_saved', 'correction_saved'].includes(status),
  correct: status === 'returned_for_correction',
  revise: status === 'returned_for_revision'
});

/**
 * Compute the "continue" flow subtype from a status value.
 * Returns 'correction' | 'revision' | 'new'
 */
export const getContinueSubtype = (status) => {
  if (status === 'correction_saved') return 'correction';
  if (status === 'revision_saved') return 'revision';
  return 'new';
};

/**
 * Reusable action buttons for a manuscript row / card / modal.
 *
 * Props:
 *   submission        - the submission object (needs revision_id or id + status)
 *   onNavigate        - optional; called BEFORE any navigation (e.g. close a modal)
 *   onContinue        - optional; (id, subtype) => void. Falls back to navigate('/submit?a=...')
 *   navigate          - optional; react-router navigate fn. Recommended for SPA routing.
 *   size              - 'sm' (default, for table rows) | 'md' (for cards/modal footer)
 *   variant           - 'buttons' (default) | 'icons' (compact icon-only)
 *   emptyFallback     - React node to render when no actions apply. Defaults to null.
 */
const ManuscriptActions = ({
  submission,
  onNavigate,
  onContinue,
  navigate,
  size = 'sm',
  variant = 'buttons',
  emptyFallback = null
}) => {
  if (!submission) return emptyFallback;

  const id = submission.revision_id || submission.id;
  const status = submission.status;
  const { continue: canContinue, correct: canCorrect, revise: canRevise } = getAvailableActions(status);

  // Sizes
  const btnBase =
    size === 'md'
      ? 'px-4 py-2 text-sm rounded-lg gap-2'
      : 'px-2.5 py-1 text-xs rounded-md gap-1';
  const iconSize = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  const preNavigate = () => {
    if (typeof onNavigate === 'function') onNavigate();
  };

  const doContinue = () => {
    preNavigate();
    const subtype = getContinueSubtype(status);
    if (typeof onContinue === 'function') {
      onContinue(id, subtype);
      return;
    }
    if (navigate) {
      // Fallback navigation if caller didn't supply a handler
      if (subtype === 'correction') {
        navigate(`/submit?a=${id}&status=correction&correction=true`);
      } else if (subtype === 'revision') {
        navigate(`/submit?a=${id}&status=revision&revision=true`);
      } else {
        navigate(`/submit?a=${id}&status=new`);
      }
    }
  };

  const doCorrect = () => {
    preNavigate();
    // Pass navigate into the util so it uses SPA routing when available
    handleStartCorrection(id, navigate ? { navigate } : undefined);
  };

  const doRevise = () => {
    preNavigate();
    handleStartRevision(id, navigate ? { navigate } : undefined);
  };

  if (!canContinue && !canCorrect && !canRevise) {
    return emptyFallback;
  }

  // Icon-only variant (e.g. tight spaces)
  if (variant === 'icons') {
    return (
      <div className="flex items-center gap-1">
        {canContinue && (
          <button
            type="button"
            onClick={doContinue}
            className="p-1.5 text-[--asfi-blue] hover:bg-blue-50 rounded-md transition-colors"
            title="Continue Submission"
          >
            <PenTool className="w-4 h-4" />
          </button>
        )}
        {canCorrect && (
          <button
            type="button"
            onClick={doCorrect}
            className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-md transition-colors"
            title="Start Correction"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
        {canRevise && (
          <button
            type="button"
            onClick={doRevise}
            className="p-1.5 text-[--asfi-blue] hover:bg-blue-50 rounded-md transition-colors"
            title="Start Revision"
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // Default: text buttons
  return (
    <div className="flex items-center gap-2">
      {canContinue && (
        <button
          type="button"
          onClick={doContinue}
          className={`inline-flex items-center font-medium text-white bg-[--asfi-blue] hover:opacity-90 transition-opacity ${btnBase}`}
          title="Continue Submission"
        >
          <PenTool className={iconSize} />
          Continue
        </button>
      )}

      {canCorrect && (
        <button
          type="button"
          onClick={doCorrect}
          className={`inline-flex items-center font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors ${btnBase}`}
          title="Start Correction"
        >
          <RotateCcw className={iconSize} />
          Correct
        </button>
      )}

      {canRevise && (
        <button
          type="button"
          onClick={doRevise}
          className={`inline-flex items-center font-medium text-white bg-[--asfi-blue] hover:opacity-90 transition-opacity ${btnBase}`}
          title="Start Revision"
        >
          <Edit className={iconSize} />
          Revise
        </button>
      )}
    </div>
  );
};

export default ManuscriptActions;